'use client';
import { cn } from '@fshr/shared-react';
import { Icon, IconType } from '../components';
import React, { FC, ReactNode, useState } from 'react';
import ChevronDown from '../icon/chevron-down';
import { useTranslations } from '@fshr/context/i18n-context';

interface ExpandableSectionProps {
	title: string | React.ReactNode;
	children: React.ReactNode;
	defaultExpanded?: boolean;
	titleIcon?: IconType;
	buttonLabels?: { collapse: string; expand: string };
	additionalClasses?: string;
	additionalIconClasses?: string;
	useChevron?: boolean;
	CustomExpandIcon?: FC<{ isExpanded?: boolean }>;
	elementsContainerClassName?: string;
	childrenClassName?: string;
	isExpanded?: boolean;
	onToggle?: () => void;
}

export default function ExpandableSection({
	title,
	children,
	defaultExpanded = false,
	titleIcon,
	buttonLabels = { collapse: 'Collapse', expand: 'Expand' },
	additionalClasses,
	additionalIconClasses,
	useChevron = false,
	CustomExpandIcon,
	elementsContainerClassName,
	childrenClassName,
	isExpanded: controlledExpanded,
	onToggle,
}: ExpandableSectionProps) {
	const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
	const isExpanded = controlledExpanded ?? internalExpanded;

	const handleToggle = () => {
		if (onToggle) {
			onToggle();
		} else {
			setInternalExpanded(!internalExpanded);
		}
	};

	const t = useTranslations();
	return (
		<div className={`flex flex-col border-b border-border-default w-full ${additionalClasses}`}>
			<div
				className={cn(
					'flex justify-between items-center py-300 cursor-pointer',
					elementsContainerClassName
				)}
				onClick={handleToggle}
			>
				<div className={cn('flex gap-100 pt-[3px]', titleIcon ? 'items-center' : 'items-baseline')}>
					{titleIcon && (
						<Icon
							icon={titleIcon}
							className={!additionalIconClasses ? 'h-200 w-200 ' : ` ${additionalIconClasses}`}
						/>
					)}
					<h3 className="text-subtitle2">{typeof title === 'string' ? t(title) : title}</h3>
				</div>
				<button
					type="button"
					className="cursor-pointer"
					aria-label={`${typeof title === 'string' ? title : 'Section'} - ${isExpanded ? buttonLabels.collapse : buttonLabels.expand}`}
				>
					{CustomExpandIcon ? (
						<CustomExpandIcon isExpanded={isExpanded} />
					) : useChevron ? (
						<ChevronDown
							className={cn(
								'h-200 w-200 transition-transform duration-300',
								isExpanded ? 'rotate-180' : ''
							)}
						/>
					) : (
						<Icon icon={isExpanded ? 'less_sign' : 'plus_sign'} className="h-200 w-200" />
					)}
				</button>
			</div>
			{isExpanded && (
				<div className={cn('flex justify-start mb-300', childrenClassName)}>{children}</div>
			)}
		</div>
	);
}
